<template>
    <dialog
        ref="dialogEl"
        class="fade-dialog-enter-exit overflow-hidden bg-transparent"
        @cancel.prevent="closeWithReason('dialog-cancel-event')"
        @click.self="closeWithReason('backdrop')"
        @close="closeWithReason('dialog-close-event')"
    >
        <div class="dialog-content flex flex-1 flex-col rounded-3xl bg-white">
            <header class="flex min-h-14 flex-row items-start justify-between gap-3 p-3 md:px-6">
                <h2 class="min-w-0 self-center break-words text-heading-sm text-content-primary">{{ title }}</h2>
                <DsIconButton
                    v-if="showCloseButton"
                    aria-label="Close"
                    data-testid="dialog-close-button"
                    icon="fa-solid fa-xmark"
                    size="sm"
                    @click="closeWithReason('icon-button')"
                />
            </header>
            <div
                ref="contentRef"
                class="dialog-slotted-content flex-1 overflow-y-auto px-4 md:px-6"
                :class="{
                    'scrolled-content': isContentScrolled,
                }"
                @scroll="refreshScrollState"
            >
                <slot />
            </div>
            <footer class="flex flex-col-reverse justify-end gap-3 p-4 md:flex-row md:px-6 md:pb-4">
                <DsButton
                    v-if="secondaryLabel"
                    size="md"
                    variant="secondary"
                    @click="$emit('clickSecondary')"
                >
                    {{ secondaryLabel }}
                </DsButton>
                <DsButton
                    :destructive="isPrimaryDestructive"
                    :leading="primaryLeading"
                    :loading="primaryLoading"
                    size="md"
                    variant="primary"
                    @click="$emit('clickPrimary')"
                >
                    {{ primaryLabel }}
                </DsButton>
            </footer>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import DsIconButton from '@/components/design-system/button/DsIconButton.vue';
import type { DsButtonProps } from '@ds/button/DsButton.vue';
import DsButton from '@ds/button/DsButton.vue';
import { ref, useTemplateRef, watch } from 'vue';

export type DsDialogCloseReason = 'icon-button' | 'backdrop' | 'dialog-close-event' | 'dialog-cancel-event';

export interface DsDialogProps {
    title: string;
    primaryLabel: string;
    isPrimaryDestructive?: DsButtonProps['destructive'];
    /** a leading icon on the primary button */
    primaryLeading?: DsButtonProps['leading'];
    secondaryLabel?: string;
    primaryLoading?: DsButtonProps['loading'];
    showCloseButton?: boolean;
}

export interface DsDialogEmits {
    clickPrimary: [];
    clickSecondary: [];
    close: [reason: DsDialogCloseReason];
    closeAnimationEnd: [reason: DsDialogCloseReason];
}

withDefaults(defineProps<DsDialogProps>(), {
    showCloseButton: true,
});

const emit = defineEmits<DsDialogEmits>();

const open = defineModel<HTMLDialogElement['open']>('open');
const closeReason = ref<DsDialogCloseReason>('dialog-close-event');
const dialogEl = useTemplateRef('dialogEl');
const contentRef = useTemplateRef('contentRef');
const isContentScrolled = ref(false);

const ANIMATION_DURATION_MS = 150;

const closeWithReason = (reason: DsDialogCloseReason) => {
    closeReason.value = reason;
    open.value = false;
};

watch(open, isOpen => {
    if (isOpen) {
        dialogEl.value?.showModal();
        closeReason.value = 'dialog-close-event';
        contentRef.value?.scrollTo({ top: 0, behavior: 'instant' });
    } else {
        dialogEl.value?.close(closeReason.value);
        emit('close', closeReason.value);
        setTimeout(emit, ANIMATION_DURATION_MS, 'closeAnimationEnd', closeReason.value); // browser's native `animationend` event not triggered because <dialog> is out of DOM after `.close()`
    }
});

const refreshScrollState = () => {
    if (!contentRef.value) return;

    isContentScrolled.value = contentRef.value.scrollTop > 0;
};
</script>

<style scoped lang="scss">
@import '$sass/dialog-fading.scss';
@import '$sass/breakpoints.scss';

dialog {
    --duration: v-bind('ANIMATION_DURATION_MS + "ms"');

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.7);
    }
    &:open {
        // Caution: only change display property when open to avoid a ghost dialog when closed
        @apply flex;
    }

    .dialog-content {
        $grid-gutter: 24px;
        $total-vertical-margin: theme('spacing.32');

        width: calc(100vw - #{$grid-gutter * 2});
        max-height: calc(100vh - $total-vertical-margin);

        @media screen and (min-width: $DS_BREAKPOINT_MD) {
            width: 560px;
        }
    }

    .dialog-slotted-content.scrolled-content {
        @apply divide-semantic-primary-default border-y;
    }
}
</style>
